<template>
  <a-space direction="vertical" style="width:100%" size="middle">
    <a-card :bordered="false">
      <a-space wrap>
        <a-button type="primary" @click="openCreate">Добавить особый вид питания</a-button>
        <a-input
          v-model:value="search"
          placeholder="Поиск по названию или сокращению"
          style="width:320px"
          allowClear
        />
        <a-button @click="load" :loading="loading">Обновить</a-button>
      </a-space>
    </a-card>

    <a-card title="Особые виды питания" :bordered="false">
      <a-table :dataSource="filteredRows" :columns="columns" :loading="loading" :pagination="false" rowKey="id">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'short_name'">
            {{ record.short_name || '—' }}
          </template>

          <template v-else-if="column.key === 'for_print'">
            <a-switch :checked="!!record.for_print" @change="toggleForPrint(record)" />
          </template>

          <template v-else-if="column.key === 'active'">
            <a-switch :checked="!!record.active" @change="toggleActive(record)" />
          </template>

          <template v-else-if="column.key === 'actions'">
            <a-button size="small" @click="openEdit(record)">Редактировать</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="editOpen"
      :title="editing?.id ? 'Редактировать особый вид питания' : 'Новый особый вид питания'"
      @ok="save"
      :confirmLoading="saving"
    >
      <a-form layout="vertical">
        <a-form-item label="Название">
          <a-input v-model:value="form.name" />
        </a-form-item>

        <a-form-item label="Короткое название">
          <a-input v-model:value="form.short_name" placeholder="Например, БМ" />
        </a-form-item>

        <a-form-item label="Выводить на печать">
          <a-switch v-model:checked="form.for_print" />
        </a-form-item>
      </a-form>
    </a-modal>
  </a-space>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  getKitchenSpecialTypes,
  createKitchenSpecialType,
  updateKitchenSpecialType,
  setKitchenSpecialTypeActive,
  setKitchenSpecialTypeForPrint,
} from '../../api/specialTypes'

const loading = ref(false)
const saving = ref(false)
const editOpen = ref(false)
const rows = ref([])
const editing = ref(null)
const search = ref('')

const form = ref({
  name: '',
  short_name: '',
  for_print: false,
})

const columns = [
  { title: 'Название', dataIndex: 'name' },
  { title: 'Коротко', key: 'short_name', width: 140 },
  { title: 'На печать', key: 'for_print', width: 110 },
  { title: 'Активен', key: 'active', width: 100 },
  { title: '', key: 'actions', width: 160 },
]

const filteredRows = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) return rows.value

  return rows.value.filter(row =>
    [row.name, row.short_name]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query)
  )
})

async function load() {
  loading.value = true
  try {
    const res = await getKitchenSpecialTypes()
    rows.value = res.data.data || []
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка загрузки')
  } finally {
    loading.value = false
  }
}

function openCreate() {
  editing.value = null
  form.value = {
    name: '',
    short_name: '',
    for_print: false,
  }
  editOpen.value = true
}

function openEdit(row) {
  editing.value = row
  form.value = {
    name: row.name || '',
    short_name: row.short_name || '',
    for_print: !!row.for_print,
  }
  editOpen.value = true
}

async function save() {
  saving.value = true
  try {
    const payload = {
      name: form.value.name,
      short_name: form.value.short_name || '',
      for_print: !!form.value.for_print,
    }

    if (editing.value?.id) {
      await updateKitchenSpecialType(editing.value.id, payload)
    } else {
      await createKitchenSpecialType(payload)
    }

    editOpen.value = false
    message.success('Сохранено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сохранения')
  } finally {
    saving.value = false
  }
}

async function toggleActive(row) {
  try {
    await setKitchenSpecialTypeActive(row.id, !row.active)
    message.success('Сохранено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка изменения активности')
  }
}

async function toggleForPrint(row) {
  try {
    await setKitchenSpecialTypeForPrint(row.id, !row.for_print)
    message.success('Сохранено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка изменения печати')
  }
}

onMounted(load)
</script>
